/**
 * Track F — SVG exporters.
 * Vector workflow: mm-unit SVG, per-layer <g> with data attrs, exact layer
 * colors; clipped pixels as <path fill-rule="evenodd">. Photo workflow:
 * v1.5-compatible structure (mopa-photo-summary / mopa-photo-layer metadata,
 * Cxx_photo_HEX_target_HEX_dots groups, optional square-run rects).
 */
import type { CutParams, PaletteMode, PhotoResult, Ring, VectorExportContext } from "../core/types";
import { LIGHTBURN_PALETTE } from "../core/types";
import { fmt } from "../core/geometry";
import { buildMopaLegend, toMopaLayerRecord } from "./paletteCsv";

// ---------- lightweight XML builder (mirrors ElementTree indent + self-close) ----------

type Attr = [string, string];

interface El {
  tag: string;
  attrs: Attr[];
  text: string | null;
  children: El[];
}

function el(tag: string, attrs: Attr[] = []): El {
  return { tag, attrs, text: null, children: [] };
}

function escAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Serialize like ElementTree with space="  " and short_empty_elements=True. */
function serialize(node: El, depth: number): string {
  const pad = "  ".repeat(depth);
  let open = "<" + node.tag;
  for (const [k, v] of node.attrs) open += ` ${k}="${escAttr(v)}"`;
  const hasChildren = node.children.length > 0;
  const hasText = node.text != null && node.text !== "";
  if (!hasChildren && !hasText) return `${pad}${open} />`;
  if (!hasChildren) return `${pad}${open}>${escText(node.text as string)}</${node.tag}>`;
  const lines = [`${pad}${open}>`];
  for (const c of node.children) lines.push(serialize(c, depth + 1));
  lines.push(`${pad}</${node.tag}>`);
  return lines.join("\n");
}

// ---------- helpers ----------

const PALETTE_MODE_LABEL: Record<PaletteMode, string> = {
  adaptive: "Adaptive photo colors",
  lightburn: "LightBurn exact layer colors",
};

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}

/** Zero-padded C-layer label body ("00".."29"; tolerates any integer). */
function c2(n: number): string {
  const i = Math.trunc(Number.isFinite(n) ? n : 0);
  const body = Math.abs(i).toString().padStart(2, "0");
  return i < 0 ? "-" + body : body;
}

function stripHash(hex: string): string {
  return hex.charAt(0) === "#" ? hex.slice(1) : hex;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = stripHash(hex);
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [
    Number.isFinite(r) ? r : 0,
    Number.isFinite(g) ? g : 0,
    Number.isFinite(b) ? b : 0,
  ];
}

function paletteColor(layer: number): string {
  const i = ((Math.trunc(layer) % LIGHTBURN_PALETTE.length) + LIGHTBURN_PALETTE.length) % LIGHTBURN_PALETTE.length;
  return LIGHTBURN_PALETTE[i];
}

/**
 * Per-dot shape element(s); cross returns two rects. `aspect` stretches height,
 * `flipY` mirrors vertically — both applied via the signed y-scale `ys` so the
 * SVG matches the on-screen preview exactly.
 */
function shapeEls(cx: number, cy: number, shape: string, size: number, color: string, aspect = 1, flipY = false): El[] {
  const r = size / 2;
  const ys = (flipY ? -1 : 1) * (aspect > 0 ? aspect : 1);
  const ry = r * Math.abs(ys);
  const common: Attr[] = [["fill", color], ["stroke", color], ["stroke-width", "0.001"]];
  const s = (shape || "circle").toLowerCase();
  const poly = (verts: [number, number][]): El =>
    el("polygon", [["points", verts.map(([x, y]) => `${fmt(x)},${fmt(y)}`).join(" ")], ...common]);
  if (s === "square") {
    // rot PI/4 four-gon == axis-aligned square; expressed as a polygon so the
    // vertical stretch applies uniformly with the other shapes.
    return [poly([[cx - r, cy - ry], [cx + r, cy - ry], [cx + r, cy + ry], [cx - r, cy + ry]])];
  }
  if (s === "star") {
    const pts: [number, number][] = [];
    for (let i = 0; i < 10; i++) {
      const a = -Math.PI / 2 + (Math.PI * i) / 5;
      const rr = i % 2 === 0 ? r : r * 0.5;
      pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr * ys]);
    }
    return [poly(pts)];
  }
  if (s === "heart") {
    const n = 48;
    let ext = 0;
    const raw: [number, number][] = [];
    for (let i = 0; i < n; i++) {
      const t = (2 * Math.PI * i) / n;
      const x = 16 * Math.pow(Math.sin(t), 3);
      const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
      raw.push([x, y]);
      ext = Math.max(ext, Math.abs(x), Math.abs(y));
    }
    const sc = ext > 0 ? r / ext : 1;
    return [poly(raw.map(([x, y]) => [cx + x * sc, cy + y * sc * ys]))];
  }
  if (s === "diamond" || s === "hexagon" || s === "octagon" || s === "triangle") {
    const sides = s === "diamond" ? 4 : s === "hexagon" ? 6 : s === "octagon" ? 8 : 3;
    // diamond: vertices on the axes (rot 0); octagon: flat top (rot PI/8); triangle: point up.
    const rot = s === "octagon" ? Math.PI / 8 : s === "triangle" ? -Math.PI / 2 : 0;
    const pts: [number, number][] = [];
    for (let i = 0; i < sides; i++) {
      const a = rot + (2 * Math.PI * i) / sides;
      pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r * ys]);
    }
    return [poly(pts)];
  }
  if (s === "cross") {
    const bar = Math.max(size * 0.33, size / 6);
    return [
      el("rect", [["x", fmt(cx - r)], ["y", fmt(cy - (bar / 2) * Math.abs(ys))], ["width", fmt(size)], ["height", fmt(bar * Math.abs(ys))], ...common]),
      el("rect", [["x", fmt(cx - bar / 2)], ["y", fmt(cy - ry)], ["width", fmt(bar)], ["height", fmt(2 * ry)], ...common]),
    ];
  }
  // circle + unknown fallback
  if (Math.abs(ys) !== 1) {
    return [el("ellipse", [["cx", fmt(cx)], ["cy", fmt(cy)], ["rx", fmt(r)], ["ry", fmt(ry)], ...common])];
  }
  return [el("circle", [["cx", fmt(cx)], ["cy", fmt(cy)], ["r", fmt(r)], ...common])];
}

/** Even-odd multi-ring path data: "M x y L x y ... Z M ... Z" (empty if all rings degenerate). */
function ringsToPathD(rings: readonly Ring[]): string {
  const parts: string[] = [];
  for (const ring of rings) {
    if (!ring || ring.length < 6) continue; // need at least 3 points
    let seg = `M ${fmt(ring[0])} ${fmt(ring[1])}`;
    for (let i = 2; i + 1 < ring.length; i += 2) seg += ` L ${fmt(ring[i])} ${fmt(ring[i + 1])}`;
    seg += " Z";
    parts.push(seg);
  }
  return parts.join(" ");
}

// ---------- vector workflow ----------

const XML_DECL = '<?xml version="1.0" encoding="UTF-8"?>\n';

export function exportVectorSvg(ctx: VectorExportContext): string {
  const { pixels, settings, layers, photoPalette } = ctx;
  const pixelSize = settings.pixelSize > 0 ? settings.pixelSize : 0.0001;

  // Photo-fill: layer index -> svg color from the photo palette (overrides layer colors).
  const photoColors = new Map<number, string>();
  if (photoPalette) {
    for (const e of photoPalette) photoColors.set(e.layerIndex, e.svgColor);
  }
  const colorFor = (layer: number): string => {
    const photo = photoColors.get(layer);
    if (photo) return photo;
    const info = layers[layer];
    return info && info.color ? info.color : paletteColor(layer);
  };

  // pad = max rendered dot size across all pixels, at least pixelSize. Aspect
  // stretches height, so the padding must account for the taller axis too.
  const aspectPad = settings.aspect > 1 ? settings.aspect : 1;
  let pad = pixelSize * aspectPad;
  for (const p of pixels) {
    const size = Math.max(0.0001, pixelSize * clamp(p.sizeFactor ?? 1, 0.05, 10)) * aspectPad;
    if (size > pad) pad = size;
  }

  // bbox of dot centers.
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const p of pixels) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    if (p.x < minx) minx = p.x;
    if (p.x > maxx) maxx = p.x;
    if (p.y < miny) miny = p.y;
    if (p.y > maxy) maxy = p.y;
  }
  if (!Number.isFinite(minx)) {
    minx = 0; miny = 0; maxx = 0; maxy = 0; // empty / all-invalid input -> degenerate box
  }
  minx -= pad; miny -= pad; maxx += pad; maxy += pad;
  const width = Math.max(maxx - minx, 0.001);
  const height = Math.max(maxy - miny, 0.001);

  const svg = el("svg", [
    ["xmlns", "http://www.w3.org/2000/svg"],
    ["version", "1.1"],
    ["width", `${fmt(width)}mm`],
    ["height", `${fmt(height)}mm`],
    ["viewBox", `${fmt(minx)} ${fmt(miny)} ${fmt(width)} ${fmt(height)}`],
  ]);
  const desc = el("desc");
  desc.text = "Pixelated vector dots generated by MOPA Pixelator for LightBurn. Units are millimeters.";
  svg.children.push(desc);

  // When a photo-fill palette is present, embed the same self-describing MOPA
  // legend the photo SVG carries, so a color-filled vector export is also a
  // reproducible burn record. Palette order is the run order.
  if (photoPalette && photoPalette.length) {
    const cutParams = ctx.cutParams;
    const metadata = el("metadata");
    metadata.children.push(el("mopa-photo-summary", [
      ["units", "power=%, speed=mm/s, frequency=kHz, pulse_width=ns, hatch_spacing=mm, hatch_angle=deg, focus_offset=mm; geometry=mm"],
      ["palette_layers", String(photoPalette.length)],
    ]));
    for (let i = 0; i < photoPalette.length; i++) {
      const e = photoPalette[i];
      const rec = toMopaLayerRecord(e, i + 1, cutParams ? cutParams[e.layerIndex] : undefined);
      const [tr, tg, tb] = hexToRgb(rec.targetColorHex);
      metadata.children.push(el("mopa-photo-layer", [
        ["run_order", String(rec.runOrder)],
        ["layer", rec.name],
        ["name", rec.name],
        ["svg_color", e.svgColor],
        ["target_photo_color", rec.targetColorHex],
        ["target_rgb", `${tr},${tg},${tb}`],
        ["pixel_count", String(e.pixelCount)],
        ["palette_error", e.paletteError.toFixed(3)],
        ["power_pct", fmt(rec.power)],
        ["speed_mm_s", fmt(rec.speed)],
        ["frequency_khz", fmt(rec.frequencyKhz)],
        ["pulse_width_ns", fmt(rec.pulseWidthNs)],
        ["hatch_spacing_mm", fmt(rec.hatchSpacingMm)],
        ["hatch_angle_deg", fmt(rec.hatchAngleDeg)],
        ["hatch_type", rec.hatchType],
        ["passes", String(rec.passes)],
        ["focus_offset_mm", fmt(rec.focusOffsetMm)],
      ]));
    }
    svg.children.push(metadata);
  }

  // group pixels by layer, ascending.
  const byLayer = new Map<number, typeof pixels>();
  for (const p of pixels) {
    let arr = byLayer.get(p.layer);
    if (!arr) { arr = []; byLayer.set(p.layer, arr); }
    arr.push(p);
  }
  const layerKeys = Array.from(byLayer.keys()).sort((a, b) => a - b);

  for (const layer of layerKeys) {
    const layerPixels = byLayer.get(layer) as typeof pixels;
    const color = colorFor(layer);
    const g = el("g", [
      ["id", `C${c2(layer)}_pixelated_${stripHash(color)}`],
      ["data-layer", `C${c2(layer)}`],
      ["data-color", color],
      ["data-dot-count", String(layerPixels.length)],
      ["fill", color],
      ["stroke", color],
    ]);
    const title = el("title");
    title.text = `C${c2(layer)}: ${layerPixels.length} pixel dots generated by MOPA Pixelator`;
    g.children.push(title);

    for (const pix of layerPixels) {
      if (pix.rings && pix.rings.length) {
        const d = ringsToPathD(pix.rings);
        if (!d) continue; // fully degenerate clip -> emit nothing
        g.children.push(el("path", [
          ["d", d],
          ["fill-rule", "evenodd"],
          ["fill", color],
          ["stroke", color],
          ["stroke-width", "0.001"],
        ]));
      } else {
        const size = Math.max(0.0001, pixelSize * clamp(pix.sizeFactor ?? 1, 0.05, 10));
        for (const e of shapeEls(pix.x, pix.y, settings.shape, size, color, settings.aspect, pix.flipY ?? false)) g.children.push(e);
      }
    }
    svg.children.push(g);
  }

  return XML_DECL + serialize(svg, 0);
}

// ---------- photo workflow ----------

export function exportPhotoSvg(
  result: PhotoResult,
  options: {
    optimizeSquareRuns: boolean;
    /** Per-layer laser params to embed in the self-describing MOPA legend. */
    cutParams?: Record<number, CutParams>;
    /** Machine/material/finish context echoed into the legend metadata. */
    context?: { machine?: string; material?: string; finish?: string };
  },
): string {
  const { cols, rows, spacingX, spacingY, pixelSize, shape, originX, originY, palette, grid } = result;

  const pad = Math.max(0, (pixelSize - Math.min(spacingX, spacingY)) / 2);
  const gridLeft = originX - spacingX / 2;
  const gridTop = originY - spacingY / 2;
  const minx = gridLeft - pad;
  const miny = gridTop - pad;
  const width = Math.max(cols * spacingX + 2 * pad, 0.001);
  const height = Math.max(rows * spacingY + 2 * pad, 0.001);

  const svg = el("svg", [
    ["xmlns", "http://www.w3.org/2000/svg"],
    ["version", "1.1"],
    ["width", `${fmt(width)}mm`],
    ["height", `${fmt(height)}mm`],
    ["viewBox", `${fmt(minx)} ${fmt(miny)} ${fmt(width)} ${fmt(height)}`],
    ["shape-rendering", "geometricPrecision"],
  ]);

  const modeLabel = PALETTE_MODE_LABEL[result.paletteMode] ?? String(result.paletteMode);

  const title = el("title");
  title.text = "MOPA Pixelator photo/graphic color layers";
  svg.children.push(title);

  const desc = el("desc");
  desc.text =
    "Photo/graphic color-layer SVG generated by MOPA Pixelator for LightBurn. " +
    `Source: ${result.sourceName}. Grid: ${cols} x ${rows}. ` +
    `Visible pixels: ${result.visibleCount}. Layers: ${palette.length}. Units are millimeters. ` +
    "Each SVG group is one color layer named Cxx_photo_HEX_dots.";
  svg.children.push(desc);

  // Full self-describing MOPA legend: the same per-layer record the CSV carries,
  // embedded as SVG metadata so the file reproduces a burn without the CSV.
  const legend = buildMopaLegend(result, options.cutParams);
  const metadata = el("metadata");
  metadata.children.push(el("mopa-photo-summary", [
    ["source", result.sourceName],
    ["source_size", `${result.sourceWidth}x${result.sourceHeight}`],
    ["grid", `${cols}x${rows}`],
    ["spacing_mm", `${fmt(spacingX)},${fmt(spacingY)}`],
    ["pixel_size_mm", fmt(pixelSize)],
    ["physical_size_mm", `${fmt(result.widthMm)}x${fmt(result.heightMm)}`],
    ["palette_mode", modeLabel],
    ["machine", options.context?.machine ?? ""],
    ["material", options.context?.material ?? ""],
    ["finish", options.context?.finish ?? ""],
    ["units", "power=%, speed=mm/s, frequency=kHz, pulse_width=ns, hatch_spacing=mm, hatch_angle=deg, focus_offset=mm; geometry=mm"],
    ["after_import", "assign each C-layer to the matching material-library recipe manually"],
  ]));
  for (let i = 0; i < palette.length; i++) {
    const e = palette[i];
    const rec = legend[i];
    const [sr, sg, sb] = hexToRgb(e.svgColor);
    const [tr, tg, tb] = hexToRgb(e.targetColor);
    metadata.children.push(el("mopa-photo-layer", [
      ["run_order", String(rec.runOrder)],
      ["layer", rec.name],
      ["name", rec.name],
      ["svg_color", e.svgColor],
      ["target_photo_color", rec.targetColorHex],
      ["svg_rgb", `${sr},${sg},${sb}`],
      ["target_rgb", `${tr},${tg},${tb}`],
      ["pixel_count", String(e.pixelCount)],
      ["percent", e.percent.toFixed(4)],
      ["palette_error", e.paletteError.toFixed(3)],
      ["power_pct", fmt(rec.power)],
      ["speed_mm_s", fmt(rec.speed)],
      ["frequency_khz", fmt(rec.frequencyKhz)],
      ["pulse_width_ns", fmt(rec.pulseWidthNs)],
      ["hatch_spacing_mm", fmt(rec.hatchSpacingMm)],
      ["hatch_angle_deg", fmt(rec.hatchAngleDeg)],
      ["hatch_type", rec.hatchType],
      ["passes", String(rec.passes)],
      ["focus_offset_mm", fmt(rec.focusOffsetMm)],
    ]));
  }
  svg.children.push(metadata);

  const shapeL = (shape || "square").toLowerCase();
  const canOptimize =
    !!options.optimizeSquareRuns &&
    shapeL === "square" &&
    Math.abs(pixelSize - spacingX) < 1e-6 &&
    Math.abs(pixelSize - spacingY) < 1e-6;

  for (let o = 0; o < palette.length; o++) {
    const e = palette[o];
    const cell = o + 1; // grid stores palette ordinal + 1 (0 = skipped)
    const color = e.svgColor;
    const g = el("g", [
      ["id", `C${c2(e.layerIndex)}_photo_${stripHash(e.svgColor)}_target_${stripHash(e.targetColor)}_dots`],
      ["data-layer", `C${c2(e.layerIndex)}`],
      ["data-lightburn-layer", `C${c2(e.layerIndex)}`],
      ["data-svg-color", e.svgColor],
      ["data-target-photo-color", e.targetColor],
      ["data-palette-error", e.paletteError.toFixed(3)],
      ["data-count", String(e.pixelCount)],
      ["fill", color],
      ["stroke", color],
    ]);
    const gt = el("title");
    gt.text = `C${c2(e.layerIndex)} ${e.svgColor} target ${e.targetColor} ${e.pixelCount} dots`;
    g.children.push(gt);

    if (canOptimize) {
      for (let row = 0; row < rows; row++) {
        const base = row * cols;
        let col = 0;
        while (col < cols) {
          if (grid[base + col] !== cell) { col++; continue; }
          const runStart = col;
          col++;
          while (col < cols && grid[base + col] === cell) col++;
          const runLen = col - runStart;
          g.children.push(el("rect", [
            ["x", fmt(gridLeft + runStart * spacingX)],
            ["y", fmt(gridTop + row * spacingY)],
            ["width", fmt(runLen * spacingX)],
            ["height", fmt(spacingY)],
            ["fill", color],
            ["stroke", color],
            ["stroke-width", "0"],
          ]));
        }
      }
    } else {
      for (let row = 0; row < rows; row++) {
        const base = row * cols;
        for (let col = 0; col < cols; col++) {
          if (grid[base + col] !== cell) continue;
          const cx = originX + col * spacingX;
          const cy = originY + row * spacingY;
          for (const shapeEl of shapeEls(cx, cy, shape, pixelSize, color)) g.children.push(shapeEl);
        }
      }
    }
    svg.children.push(g);
  }

  const paletteNote = el("desc");
  paletteNote.text =
    "Palette: " +
    palette
      .map((e) => `C${c2(e.layerIndex)}=${e.svgColor}->target ${e.targetColor}=${e.pixelCount}`)
      .join("; ");
  svg.children.push(paletteNote);

  return serialize(svg, 0);
}
